import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { MapPin, ExternalLink, ArrowLeft, RefreshCw } from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("nb-NO", {
  day: "2-digit", month: "2-digit", year: "2-digit",
  hour: "2-digit", minute: "2-digit",
});

export default function LocationsPage() {
  const [params] = useSearchParams();
  const focus = params.get("focus");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/locations");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Auto-scroll & highlight focused location
  useEffect(() => {
    if (!focus) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`loc-${focus}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    return () => clearTimeout(t);
  }, [focus, items]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Posisjoner</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Delte posisjoner</h1>
          <p className="text-sm text-[#2B3F4D] mt-1 max-w-2xl">
            Kunder kan dele sin posisjon fra portalen for å gjøre det enklere å bistå.
            Listen viser nyeste først.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="loc-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Oppdater
        </Button>
      </div>

      {loading && (
        <Card className="p-8 text-sm text-[#2B3F4D] text-center">Laster…</Card>
      )}

      {!loading && items.length === 0 && (
        <Card className="p-8 text-center" data-testid="loc-empty">
          <MapPin className="w-10 h-10 mx-auto text-[#A57F39]" />
          <div className="text-sm text-[#2B3F4D] mt-3">
            Ingen kunder har delt posisjon ennå.
          </div>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {items.map((l) => {
          const isFocus = l.id === focus;
          return (
            <Card
              key={l.id}
              id={`loc-${l.id}`}
              data-testid={`loc-card-${l.id}`}
              className={`p-5 ${isFocus ? "ring-2 ring-[#BD984D]" : ""}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] border-0">
                      <MapPin className="w-3 h-3 mr-1" /> Posisjon
                    </Badge>
                    {l.boat_name && (
                      <Badge className="bg-[#BD984D]/15 text-[#A57F39] border-0">
                        {l.boat_name}
                      </Badge>
                    )}
                  </div>
                  <div className="font-semibold text-[#0C1824] mt-2">
                    {l.user_name}
                  </div>
                  <div className="text-xs text-[#2B3F4D] mt-1">
                    {fmt(l.created_at)}
                  </div>
                  <div className="text-xs font-mono text-slate-500 mt-2">
                    {l.latitude.toFixed(5)}, {l.longitude.toFixed(5)}
                    {l.accuracy && ` · ±${Math.round(l.accuracy)} m`}
                  </div>
                  {l.note && (
                    <div className="text-sm text-[#0C1824] mt-2 italic">«{l.note}»</div>
                  )}
                </div>
                <a href={l.google_maps_url} target="_blank" rel="noreferrer"
                   data-testid={`loc-open-${l.id}`}>
                  <Button size="sm" className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Åpne i kart
                  </Button>
                </a>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
